import React from "react";
import { cn } from "@/lib/utils";
import { PageLoader } from "@/components/common/Loading";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, ChevronsUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { useColumnWidths, ResizeHandle, ScrollTableShell } from "@/components/common/ResizableTable";

function HeaderLabel({ c, onSort, sortField, sortOrder }) {
  const sortable = c.sortKey && onSort;
  if (!sortable) return c.header;
  const active = sortField === c.sortKey;
  return (
    <button type="button" onClick={() => onSort(c.sortKey)} data-testid={`sort-${c.sortKey}`}
      className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-slate-700 max-w-full">
      <span className="truncate">{c.header}</span>
      {active ? (sortOrder === "asc" ? <ArrowUp className="w-3 h-3 shrink-0" /> : <ArrowDown className="w-3 h-3 shrink-0" />)
        : <ChevronsUpDown className="w-3 h-3 text-slate-300 shrink-0" />}
    </button>
  );
}

const isUtil = (c) => c.key.startsWith("__");
const headerText = (c) => (typeof c.header === "string" ? c.header : c.key);

function Pager({ total, page, totalPages, onPageChange }) {
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50/40 text-xs text-slate-500">
      <span>{total} total · page {page} of {totalPages}</span>
      <div className="flex gap-1">
        <Button variant="outline" size="sm" data-testid="table-prev" disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}><ChevronLeft className="w-4 h-4" /></Button>
        <Button variant="outline" size="sm" data-testid="table-next" disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}><ChevronRight className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}

// Resizable variant: per-user widths, Left/Right toolbar, sticky header, frozen lead column and actions on desktop.
function ResizableDataTable({ columns, rows, onRowClick, rowKey, resizeKey, sortField, sortOrder, onSort, testId, footer, viewExtra }) {
  const keys = columns.map((c) => c.key);
  const lead = columns.findIndex((c) => !isUtil(c));
  const defaults = columns.map((c, i) => c.width || (isUtil(c) ? 60 : i === lead ? 220 : 160));
  const pinned = columns.map((c, i) => (isUtil(c) ? i : -1)).filter((i) => i >= 0);
  const cols = useColumnWidths(resizeKey, defaults, 72, keys, pinned);
  const W = cols.widths;
  const last = columns.length - 1;
  const leftOf = (i) => W.slice(0, i).reduce((a, b) => a + b, 0);
  const freeze = (i) => {
    if (i <= lead) return { cls: cn("md:sticky md:z-10 md:left-[var(--l)]", i === lead && "md:shadow-[1px_0_0_#E2E8F0]"), style: { "--l": `${leftOf(i)}px` } };
    if (i === last && isUtil(columns[i])) return { cls: "md:sticky md:right-0 md:z-10 md:shadow-[-1px_0_0_#E2E8F0]", style: undefined };
    return { cls: "", style: undefined };
  };
  return (
    <div data-testid={testId}>
      <ScrollTableShell cols={cols} testId={`${testId}-shell`} viewsKey={`${resizeKey}-views`}
        getExtra={viewExtra?.get} applyExtra={viewExtra?.apply}>
        <table className="text-sm" style={{ tableLayout: "fixed", width: cols.total, minWidth: "100%" }}>
          <colgroup>{W.map((w, i) => <col key={keys[i]} style={{ width: w }} />)}</colgroup>
          <thead>
            <tr>
              {columns.map((c, i) => {
                const f = freeze(i);
                return (
                  <th key={c.key} style={f.style} data-testid={`${testId}-col-${c.key}`} className={cn(
                    "relative sticky top-0 z-20 bg-slate-50 border-b border-slate-200 text-left py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap overflow-hidden text-ellipsis",
                    isUtil(c) ? "px-3" : "px-4", f.cls, f.cls && "md:z-30")}>
                    <HeaderLabel c={c} onSort={onSort} sortField={sortField} sortOrder={sortOrder} />
                    {!isUtil(c) && <ResizeHandle index={i} width={W[i]} onResize={cols.setWidth} label={headerText(c)} />}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r[rowKey]} data-testid={`row-${r[rowKey]}`} onClick={onRowClick ? () => onRowClick(r) : undefined}
                className={cn("group border-b border-slate-100 last:border-0 text-slate-700", onRowClick && "cursor-pointer")}>
                {columns.map((c, i) => {
                  const f = freeze(i);
                  return (
                    <td key={c.key} style={f.style} className={cn("py-3 align-middle overflow-hidden text-ellipsis bg-white transition-colors", isUtil(c) ? "px-3" : "px-4",
                      onRowClick && "group-hover:bg-slate-50", f.cls, c.className)}>
                      {c.render ? c.render(r) : r[c.key]}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </ScrollTableShell>
      {footer}
    </div>
  );
}

export function DataTable({ columns, rows, loading, empty, onRowClick, rowKey = "id",
  page, pageSize, total, onPageChange, sortField, sortOrder, onSort, testId = "data-table", resizeKey, viewExtra }) {
  if (loading && !(resizeKey && rows?.length)) return <PageLoader />;
  if (!rows?.length) return empty || <EmptyState title="Nothing here yet" description="No records found." />;

  const totalPages = total != null && pageSize ? Math.max(1, Math.ceil(total / pageSize)) : null;
  const pager = totalPages && <Pager total={total} page={page} totalPages={totalPages} onPageChange={onPageChange} />;

  if (resizeKey) {
    return <ResizableDataTable columns={columns} rows={rows} onRowClick={onRowClick} rowKey={rowKey} resizeKey={resizeKey}
      sortField={sortField} sortOrder={sortOrder} onSort={onSort} testId={testId} viewExtra={viewExtra}
      footer={pager && <div className="mt-2 rounded-lg border border-slate-200 bg-white overflow-hidden">{pager}</div>} />;
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid={testId}>
      <div className="overflow-x-auto tr-scroll">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50/80 border-b border-slate-200">
              {columns.map((c) => (
                <th key={c.key} className={cn(
                  "text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap",
                  c.headerClassName)}>
                  <HeaderLabel c={c} onSort={onSort} sortField={sortField} sortOrder={sortOrder} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r[rowKey]}
                data-testid={`row-${r[rowKey]}`}
                onClick={onRowClick ? () => onRowClick(r) : undefined}
                className={cn("border-b border-slate-100 last:border-0 text-slate-700 transition-colors",
                  onRowClick && "hover:bg-slate-50/70 cursor-pointer")}
              >
                {columns.map((c) => (
                  <td key={c.key} className={cn("px-4 py-3 align-middle", c.className)}>
                    {c.render ? c.render(r) : r[c.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pager}
    </div>
  );
}
